// LinkedProductCard - one supplier listing at one ticked "Show first" branch,
// with quick add to cart controls (QA-001). Shown only in prioritised mode;
// products no ticked branch can supply keep the grouped SearchProductCard.
//
// The top of the card opens the supplier product page; the footer adds to
// the cart without leaving search. The branch is the card's own — the
// engineer never picks it here.
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Heart,
  ShoppingCart,
  Store,
  Truck,
  Wrench,
} from 'lucide-react'
import { BackorderBadge } from '../radix/BackorderBadge'
import { CheckingPriceBadge } from '../radix/CheckingPriceBadge'
import { InStockBadge } from '../radix/InStockBadge'
import { QuantitySelector } from '../radix/QuantitySelector'
import { useToast } from '../radix/Toast'
import { useCart } from '../../auth/hooks/useCart'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useFavouriteToggle } from './favourites/useFavouriteToggle'
import { useDebounce } from '../../hooks/useDebounce'
import { api } from '../../services/api'
import {
  writeCartFulfillmentMethod,
  writeItemFulfillmentMethod,
} from '../../utils/cartFulfillment'
import {
  defaultQuickAddMethod,
  quickAddBackorderWarning,
  quickAddCanBackorder,
  quickAddConfirmation,
  quickAddMethodOrderable,
  quickAddMethodStock,
  quickAddNeedsQuantityCheck,
  quickAddNotAvailableLabel,
  quickAddOnlyInStockMessage,
  quickAddStockLabel,
  withQuantityCheck,
} from '../../utils/quickAdd'
import type { LinkedProductCardInfo } from '../../utils/branchPriority'
import type { QuickAddQuantityCheckItem } from '../../utils/quickAdd'
import type { CartFulfillmentMethod } from '../../utils/cartFulfillment'
import type { MasterProduct } from '../../types'
import { HOME_TAB_ENABLED } from '../../config/features'
import { MAX_CART_LINE_QUANTITY, PRODUCT_ADD_MODES } from '../../constants/quickAdd'

// How long the button shows the green "Added" state (QA-007 AC3).
const ADDED_STATE_MS = 2500
// Pause after the last quantity change before asking the supplier again.
const QUANTITY_CHECK_DEBOUNCE_MS = 500
// How long a quantity check's answer is reused for the same quantity.
const QUANTITY_CHECK_STALE_MS = 2 * 60 * 1000

export interface LinkedProductCardProps {
  // The grouped product this listing belongs to (image, fallback name).
  product: MasterProduct
  card: LinkedProductCardInfo
  // 1-indexed position in the visible result list, for analytics.
  position: number
  // This card's place among its supplier's cards in the list (1 = first),
  // for the quick add analytics event; null when not known.
  supplierPosition: number | null
  // True while the live price for this listing is still on its way.
  isPriceRefreshing?: boolean
  // Search postcode, for the quantity-specific stock re-check.
  postcode: string | null
  // Pack size from the live price refresh (CEF can change it); null when the
  // refresh reported none, so the search's pack size stands.
  livePackSize: number | null
  onOpen: (
    product: MasterProduct,
    card: LinkedProductCardInfo,
    position: number,
    method: CartFulfillmentMethod,
  ) => void
}

const METHOD_OPTIONS: ReadonlyArray<{
  method: CartFulfillmentMethod
  label: string
  Icon: typeof Store
}> = [
  { method: 'pickup', label: 'Collection', Icon: Store },
  { method: 'delivery', label: 'Delivery', Icon: Truck },
]

export function LinkedProductCard({
  product,
  card,
  position,
  supplierPosition,
  isPriceRefreshing = false,
  postcode,
  livePackSize,
  onOpen,
}: LinkedProductCardProps) {
  const { listing: searchListing } = card
  const productName = searchListing.productName || product.displayName
  const packSize = Math.max(livePackSize ?? searchListing.quantityMultiple, 1)
  const { addToast } = useToast()
  const { addToCart, cart, isAdding } = useCart()
  const { isFavourited } = useFavourites()
  const toggleFavourite = useFavouriteToggle()

  // Only the methods this branch serves can be picked.
  const methodOptions = METHOD_OPTIONS.filter(({ method }) =>
    method === 'pickup' ? searchListing.canCollect : searchListing.canDeliver,
  )
  // QA-002: the card follows the cart — this product's own line here, else
  // the basket's method at this branch (Collection when there is none, or
  // both) — until the engineer taps the toggle; then their choice stays.
  const [pickedMethod, setPickedMethod] = useState<CartFulfillmentMethod | null>(null)
  // This product's own line at this branch, if any — the card starts on its
  // method, since adding goes onto that line.
  const productLine = cart.find(
    (line) =>
      String(line.productId) === card.rawId && String(line.branchId) === card.branchId,
  )
  const preferredMethod = defaultQuickAddMethod(
    productLine?.fulfillmentMethod ?? null,
    card.basketMethods,
    searchListing.canCollect,
    searchListing.canDeliver,
  )
  // Never start on a method that can't be ordered when the other one can
  // (e.g. a backorder dated for delivery only) — the engineer can still tap it.
  const otherMethod: CartFulfillmentMethod = preferredMethod === 'pickup' ? 'delivery' : 'pickup'
  const otherMethodServed =
    otherMethod === 'pickup' ? searchListing.canCollect : searchListing.canDeliver
  const startingMethod =
    !quickAddMethodOrderable(searchListing, preferredMethod) &&
    otherMethodServed &&
    quickAddMethodOrderable(searchListing, otherMethod)
      ? otherMethod
      : preferredMethod
  const method = pickedMethod ?? startingMethod
  const [quantity, setQuantity] = useState<number | null>(packSize)
  // When the live refresh changes the pack size, a quantity the engineer
  // hasn't touched (still the old pack) follows it; a typed one is kept and
  // the whole-pack rule below flags it if it no longer fits.
  const previousPackSizeRef = useRef(packSize)
  useEffect(() => {
    const previousPackSize = previousPackSizeRef.current
    previousPackSizeRef.current = packSize
    if (previousPackSize === packSize) return
    setQuantity((current) => (current === previousPackSize ? packSize : current))
  }, [packSize])
  // Adding puts the quantity on top of this branch's line (lines at other
  // branches are left alone), so stock checks use that line's total after
  // the add, not just the typed number.
  const quantityInCart = productLine?.quantity ?? 0
  const lineQuantityAfterAdd = quantity === null ? null : quantity + quantityInCart

  // Above the branch stock the search's 1-unit answer can lack the warehouse
  // date (Wolseley / UKPS) or, for Wolseley, the backorder date for this
  // quantity — so, as on the detail page, ask the supplier once more at the
  // engineer's quantity after they stop changing it. One call returns both.
  const debouncedQuantity = useDebounce(lineQuantityAfterAdd, QUANTITY_CHECK_DEBOUNCE_MS)
  const productCode = searchListing.productCode
  const shouldCheckQuantity =
    postcode !== null &&
    productCode !== null &&
    quickAddNeedsQuantityCheck(searchListing, method, debouncedQuantity)
  const quantityCheck = useQuery({
    queryKey: [
      'quick-add-quantity-check',
      card.supplierId,
      card.branchId,
      productCode,
      debouncedQuantity,
      postcode,
    ],
    queryFn: async (): Promise<QuickAddQuantityCheckItem | null> => {
      const response = (await api.checkStock({
        supplier_id: Number(card.supplierId),
        product_codes: [productCode ?? ''],
        branch_id: card.branchId,
        postcode: postcode ?? undefined,
        quantity: debouncedQuantity ?? undefined,
        bypass_cache: true,
      })) as { items?: QuickAddQuantityCheckItem[] }
      const items = response?.items ?? []
      return items.find((item) => item.product_code === productCode) ?? items[0] ?? null
    },
    enabled: shouldCheckQuantity,
    staleTime: QUANTITY_CHECK_STALE_MS,
    retry: false,
  })
  // The answer applies only to the quantity now on the card; until it lands
  // (or if the call fails) the search's own figures stand.
  const checkedItem =
    shouldCheckQuantity && debouncedQuantity === lineQuantityAfterAdd
      ? quantityCheck.data
      : null
  const listing = checkedItem ? withQuantityCheck(searchListing, checkedItem) : searchListing
  // Add waits for the answer, as the detail page does, so a quantity the
  // supplier can't backorder is never added; a failed check doesn't block.
  const isCheckingQuantity =
    postcode !== null &&
    productCode !== null &&
    quickAddNeedsQuantityCheck(searchListing, method, lineQuantityAfterAdd) &&
    (debouncedQuantity !== lineQuantityAfterAdd || quantityCheck.isFetching)
  // QA-007 AC3: quantity just added, while the button shows "Added"; null
  // otherwise. Add is blocked meanwhile so a double tap can't add twice.
  const [justAddedQuantity, setJustAddedQuantity] = useState<number | null>(null)
  const addedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (addedTimerRef.current) clearTimeout(addedTimerRef.current)
    },
    [],
  )

  // The heart saves this supplier listing (as the supplier product page does).
  const favTarget = { type: 'raw' as const, id: card.rawId }
  const favourited = isFavourited(favTarget)

  // QA-002 AC5 / QA-003: the selected method's own stock, with dates.
  const stockLabel = quickAddStockLabel(listing, method)
  const knownPrice = card.price != null && card.price > 0 ? card.price : null
  // QA-004: whole packs only, as on the detail page — a quantity between
  // packs blocks Add (it is never rounded for the engineer).
  // Both the typed number and the line it lands on must be whole packs: a
  // line left at an older pack size can make a valid increment invalid.
  const isInvalidMultiple =
    packSize > 1 &&
    quantity !== null &&
    quantity > 0 &&
    (quantity % packSize !== 0 ||
      (lineQuantityAfterAdd !== null && lineQuantityAfterAdd % packSize !== 0))
  // QA-004 AC4/AC5: above what the method can supply (the larger of branch
  // and warehouse, as on the detail page), a supplier that can't
  // backorder gets an "Only X in stock" note and Add is disabled until the
  // quantity fits (the typed number is kept, not capped). With backorder,
  // there is no limit here.
  const methodStock = quickAddMethodStock(listing, method)
  // No stock and no backorder for this method: it can't be ordered at all.
  // No verdict while the supplier is still being asked — its answer can
  // bring a backorder date (Wolseley) that makes the method orderable; the
  // card says "Checking availability…" meanwhile.
  const isMethodUnavailable =
    !isCheckingQuantity && !quickAddMethodOrderable(listing, method)
  const showOnlyInStock =
    !isCheckingQuantity &&
    !isMethodUnavailable &&
    lineQuantityAfterAdd !== null &&
    lineQuantityAfterAdd > methodStock.capacityQty &&
    !quickAddCanBackorder(listing, method)
  // QA-005: above what the method can supply, where it can backorder — a
  // note only; Add still works. Counts the whole line after the add, since
  // all of it goes on backorder.
  const backorderWarning = quickAddBackorderWarning(listing, method, lineQuantityAfterAdd)
  // The line after the add can't go over the cart's per-line limit.
  const exceedsLineLimit =
    lineQuantityAfterAdd !== null && lineQuantityAfterAdd > MAX_CART_LINE_QUANTITY
  const canAdd =
    quantity !== null &&
    quantity > 0 &&
    !isInvalidMultiple &&
    !showOnlyInStock &&
    !isMethodUnavailable &&
    !exceedsLineLimit &&
    !isCheckingQuantity &&
    !isAdding &&
    justAddedQuantity === null

  // QA-004 AC3: a typed quantity below one pack goes back to one pack when
  // the engineer leaves the box (an emptied box too).
  const restoreMinimumQuantity = () => {
    if (quantity === null || quantity < packSize) setQuantity(packSize)
  }

  const handleAdd = () => {
    if (quantity === null || quantity <= 0) return
    const addedQuantity = quantity
    addToCart(
      {
        product_id: Number(card.rawId),
        branch_id: card.branchId,
        quantity: addedQuantity,
        fulfillment_method: method,
        // Quick add puts the quantity on top of this branch's line, if any.
        add_to_existing: true,
        // Analytics only — stripped before the request.
        inStockAtBranch: card.rank === 'in_stock',
        productAddMode: {
          mode: PRODUCT_ADD_MODES.quickAdd,
          productPosition: supplierPosition,
        },
      },
      {
        onSuccess: (cartItem) => {
          writeCartFulfillmentMethod(card.supplierId, card.branchId, method)
          if (cartItem?.id) writeItemFulfillmentMethod(cartItem.id, method)
          const confirmation = quickAddConfirmation(addedQuantity, productName)
          addToast(confirmation.message, 'success', 2000, undefined, confirmation.title)
          setJustAddedQuantity(addedQuantity)
          if (addedTimerRef.current) clearTimeout(addedTimerRef.current)
          // QA-006 AC3: when "Added" ends, the quantity goes back to one pack
          // (1 for most products) ready for the next add.
          addedTimerRef.current = setTimeout(() => {
            setJustAddedQuantity(null)
            setQuantity(packSize)
          }, ADDED_STATE_MS)
        },
        onError: (error) => {
          addToast(
            `Failed to add to basket: ${error instanceof Error ? error.message : 'Unknown error'}`,
            'error',
            3000,
          )
        },
      },
    )
  }

  return (
    <div
      // Same subtle brand-tinted border as the prioritised card it replaces.
      className="relative rounded-2xl border bg-white"
      style={{ borderColor: 'color-mix(in oklab, var(--brand-primary) 25%, transparent)' }}
    >
      <button
        type="button"
        onClick={() => onOpen(product, card, position, method)}
        className="w-full rounded-t-2xl p-4 sm:p-5 text-left hover:shadow-sm active:bg-gray-50 transition-all"
      >
        <div className="flex gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt={productName} className="w-full h-full object-contain" />
            ) : (
              <Wrench className="w-8 h-8 text-gray-300" />
            )}
          </div>

          <div className="flex-1 min-w-0 pr-8">{/* pr-8 leaves room for the heart overlay */}
            <h3 className="text-base sm:text-lg font-bold leading-tight mb-1 text-gray-900">
              {productName}
            </h3>

            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-brand-primary-tint px-2 py-0.5 text-[10px] font-semibold text-brand-primary-dark">
                <Check className="h-2.5 w-2.5" aria-hidden="true" />
                Prioritised
              </span>
              {card.rank === 'in_stock' && isMethodUnavailable ? (
                <span className="inline-block whitespace-nowrap rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                  {quickAddNotAvailableLabel(method)}
                </span>
              ) : card.rank === 'in_stock' ? (
                <InStockBadge label={stockLabel} />
              ) : (
                <BackorderBadge variant="amber-plain" />
              )}
            </div>

            {listing.productCode && (
              <p className="text-gray-400 text-xs mb-2">Product code: {listing.productCode}</p>
            )}

            {/* This branch's own price, supplier and distance — never the
             * cheapest across suppliers. */}
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-base sm:text-lg">
              {knownPrice === null && isPriceRefreshing ? (
                <CheckingPriceBadge />
              ) : (
                <span className="text-brand-primary font-bold">
                  {knownPrice !== null ? `£${knownPrice.toFixed(2)}` : '-'}
                </span>
              )}
              <span className="text-gray-600 text-xs sm:text-sm">
                · <span className="font-semibold text-gray-800">{card.supplierName}</span>
                {' · '}
                {card.branchName}
                {listing.distanceMi != null && <> · {listing.distanceMi.toFixed(1)} mi</>}
              </span>
            </div>
          </div>

          <ChevronRight aria-hidden="true" className="self-center flex-shrink-0 w-5 h-5 text-gray-400" />
        </div>
      </button>

      {/* Quick add. A sibling of the card button, so nothing in here opens
       * the product page. */}
      <div className="space-y-3 border-t border-gray-100 px-4 pb-4 pt-3 sm:px-5">
        {methodOptions.length > 1 ? (
          <div
            role="radiogroup"
            aria-label="Collection or delivery"
            className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1"
          >
            {methodOptions.map(({ method: option, label, Icon }) => {
              const isSelected = option === method
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setPickedMethod(option)}
                  className={`flex min-h-[41px] items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-colors ${
                    isSelected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </button>
              )
            })}
          </div>
        ) : methodOptions.length === 1 ? (
          <p className="text-xs font-medium text-gray-600">{methodOptions[0].label} only</p>
        ) : null}

        {/* QA-006: how many of this product are already in the cart at
         * this branch. Reads the shared cart, so it updates on add and is
         * gone once the line is removed on the cart page. Hidden while the
         * "Only X in stock" note shows — the note already says it. */}
        {productLine && productLine.quantity > 0 && !showOnlyInStock && (
          <div className="flex items-center gap-1.5">
            <ShoppingCart className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
            <span className="text-xs font-medium text-gray-600">
              {productLine.quantity.toLocaleString()} in cart
            </span>
          </div>
        )}

        {backorderWarning && (
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-orange-500" aria-hidden="true" />
            <span className="text-xs font-medium text-orange-600">{backorderWarning}</span>
          </div>
        )}

        <div onBlur={restoreMinimumQuantity}>
          <QuantitySelector
            value={quantity}
            onChange={setQuantity}
            multiple={packSize}
            size="md"
            fullWidth
            compact
            warning={isInvalidMultiple}
          />
          {showOnlyInStock && (
            <p className="mt-1.5 text-xs font-medium text-amber-600">
              {quickAddOnlyInStockMessage(methodStock.capacityQty, quantityInCart, packSize)}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className={`flex min-h-[45px] w-full items-center justify-center gap-2 rounded-xl px-3 text-base font-semibold transition-colors ${
            justAddedQuantity !== null
              ? 'bg-emerald-600 text-white'
              : 'bg-brand-primary text-[var(--brand-primary-text)] hover:bg-brand-primary-hover active:bg-brand-primary-dark disabled:opacity-50'
          }`}
        >
          {justAddedQuantity !== null ? (
            <>
              <Check className="h-5 w-5" aria-hidden="true" />
              {`Added ${justAddedQuantity.toLocaleString()}`}
            </>
          ) : (
            <>
              <ShoppingCart className="h-5 w-5" aria-hidden="true" />
              {isAdding
                ? 'Adding…'
                : isMethodUnavailable
                  ? quickAddNotAvailableLabel(method)
                  : isCheckingQuantity
                    ? 'Checking availability…'
                    : exceedsLineLimit
                      ? `Max ${MAX_CART_LINE_QUANTITY.toLocaleString()} per item`
                      : isInvalidMultiple
                        ? `Sold in multiples of ${packSize}`
                        : `Add ${quantity ?? packSize} to cart`}
            </>
          )}
        </button>
      </div>

      {HOME_TAB_ENABLED && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            toggleFavourite({ target: favTarget, productName })
          }}
          // The ::before extends the tap area to 44px without changing the look.
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-sm hover:bg-white transition-colors before:absolute before:-inset-2 before:content-['']"
          aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={favourited}
        >
          <Heart
            className={`w-4 h-4 ${favourited ? 'text-brand-primary' : 'text-gray-400'}`}
            fill={favourited ? 'currentColor' : 'none'}
          />
        </button>
      )}
    </div>
  )
}
